import useSocketReconnect from '../../utils/useSocketReconnect';
import { useEffect, useState } from "react";
import { fetchAPI, applyInverseTimeZone } from "../../utils/utils";
import moment from "moment";
import 'moment/locale/ca';
import { getCastellerDisplayName } from "../../utils/castellerName";
import { formatMoney } from "../../utils/money";
import { guestLabel, useFormGuests } from './utils/formGuests';

function FormLogs({ selectedEvent, castellersInfo, socket, userInfo }) {
    const formGuests = useFormGuests(selectedEvent, socket);
    useSocketReconnect(socket, () => {
        if (selectedEvent) socket.emit('.load_form', selectedEvent, userInfo?.id);
    });

    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('');
    const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
    const [formData, setFormData] = useState(null);
    const [itemsToShow, setItemsToShow] = useState(25);

    const loadLogs = () => {
        if (selectedEvent) {
            setLoading(true);
            fetchAPI(`/form_logs/${selectedEvent}`, (data) => {
                setLogs(data);
                setLoading(false);
            });
        }
    };

    // Load form data to resolve question names and option texts
    useEffect(() => {
        if (selectedEvent && socket) {
            socket.emit('.load_form', selectedEvent, userInfo?.id);

            const handleLoadedForm = (loadedData) => {
                setFormData(loadedData);
            };

            socket.on('.loaded_form', handleLoadedForm);

            return () => {
                socket.off('.loaded_form', handleLoadedForm);
            };
        }
    }, [selectedEvent, socket, userInfo]);

    useEffect(() => {
        loadLogs();
    }, [selectedEvent]);

    // Listen for form logs updates
    useEffect(() => {
        if (socket) {
            const handleLogsUpdated = (data) => {
                if (data.evId === selectedEvent) {
                    loadLogs();
                }
            };

            socket.on('.form_logs_updated', handleLogsUpdated);

            return () => {
                socket.off('.form_logs_updated', handleLogsUpdated);
            };
        }
    }, [selectedEvent, socket]);

    // Helper function to resolve showIf display with real question names and option texts
    const resolveShowIfDisplay = (showIf) => {
        if (!showIf || !formData) {
            return 'sense condició';
        }

        const question = formData.elements?.find(el => el.id === showIf.questionId);
        if (!question) {
            return `pregunta ${showIf.questionId} que sigui ${showIf.optionId}`;
        }

        const questionName = question.content?.question || 'Pregunta sense títol';
        
        if (question.content?.options && Array.isArray(question.content.options)) {
            const optionIndex = parseInt(showIf.optionId);
            const optionText = question.content.options[optionIndex];
            if (optionText) {
                return `pregunta "${questionName}" que sigui "${optionText}"`;
            }
        }

        return `pregunta "${questionName}" que sigui ${showIf.optionId}`;
    };

    const parseLogLine = (line) => {
        const parts = line.split(',');
        if (parts.length < 3) return null;
        
        const timestamp = parts[0];
        const userIdRaw = parts[1];
        const action = parts.slice(2).join(','); // Handle actions that might contain commas
        
        // Handle userId parsing more gracefully
        let userId;
        if (userIdRaw === 'anonymous' || userIdRaw === 'system') {
            userId = userIdRaw;
        } else if (/^\d+$/.test(userIdRaw)) {
            userId = parseInt(userIdRaw);
        } else {
            // Alphanumeric IDs (e.g. "pvv55j", "78uyn") are visitor/non-registered users
            userId = 'visitor';
        }
        
        const ts = moment(timestamp);
        if (!ts.isValid()) return null;

        return {
            timestamp: ts,
            userId,
            action,
            rawLine: line
        };
    };

    const getActionDisplay = (action) => {
        if (action === 'create') return '🆕 Formulari creat';
        if (action === 'update') return '✏️ Formulari modificat';
        if (action === 'submit') return '📤 Resposta enviada';
        if (action === 'delete') return '🗑️ Formulari eliminat';
        if (action === 'order_changed') return '🔄 Ordre de les preguntes canviat';
        
        // Handle detailed form changes
        if (action.includes(':')) {
            const parts = action.split(':');
            
            // Handle element changes FIRST (before generic -> patterns)
            if (parts[0] === 'element_added') {
                const [, type, ...questionParts] = parts;
                let question = questionParts.join(':'); // Handle questions with colons
                
                // Special handling for resum-pagaments
                if (type === 'resum-pagaments' && (question === '' || question === 'undefined' || question === '[object Object]')) {
                    question = 'Resum de pagaments';
                }
                
                // Special handling for stripe-payment
                if (type === 'stripe-payment' && (question === '' || question === 'undefined' || question === '[object Object]')) {
                    question = 'Pagar amb targeta';
                }
                
                const typeTranslations = {
                    'ticket': 'Entrada/Pagament',
                    'ticket-v2': 'Entrades múltiples',
                    'ticket-v2-single': 'Entrada única',
                    'multiple-choice': 'Selecció múltiple',
                    'checkbox': 'Caselles de verificació',
                    'short-answer': 'Resposta curta',
                    'paragraph': 'Paràgraf',
                    'image-upload': 'Pujada d\'imatge',
                    'resum-pagaments': 'Resum de pagaments',
                    'text': 'Text informatiu',
                    'assistance': 'Assistència',
                    'stripe-payment': 'Pagament amb targeta'
                };
                const typeDisplay = typeTranslations[type] || type;
                return `➕ Pregunta afegida: "${question}" (${typeDisplay})`;
            }
            
            if (parts[0] === 'element_removed') {
                const [, type, ...questionParts] = parts;
                let question = questionParts.join(':'); // Handle questions with colons
                
                // Special handling for resum-pagaments
                if (type === 'resum-pagaments' && (question === '' || question === 'undefined' || question === '[object Object]')) {
                    question = 'Resum de pagaments';
                }
                
                // Special handling for stripe-payment
                if (type === 'stripe-payment' && (question === '' || question === 'undefined' || question === '[object Object]')) {
                    question = 'Pagar amb targeta';
                }
                
                const typeTranslations = {
                    'ticket': 'Entrada/Pagament',
                    'ticket-v2': 'Entrades múltiples',
                    'ticket-v2-single': 'Entrada única',
                    'multiple-choice': 'Selecció múltiple',
                    'checkbox': 'Caselles de verificació',
                    'short-answer': 'Resposta curta',
                    'paragraph': 'Paràgraf',
                    'image-upload': 'Pujada d\'imatge',
                    'resum-pagaments': 'Resum de pagaments',
                    'text': 'Text informatiu',
                    'assistance': 'Assistència',
                    'stripe-payment': 'Pagament amb targeta'
                };
                const typeDisplay = typeTranslations[type] || type;
                return `➖ Pregunta eliminada: "${question}" (${typeDisplay})`;
            }
            
            if (parts[0] === 'element_modified') {
                // Format: element_modified:type:question:field:oldValue->newValue
                const [, type, ...remainingParts] = parts;
                
                // Find the part that contains '->' to identify where the change starts
                let fieldIndex = -1;
                for (let i = remainingParts.length - 1; i >= 0; i--) {
                    if (remainingParts[i].includes('->')) {
                        // The field name is one part before the value change
                        fieldIndex = i - 1;
                        break;
                    }
                }
                
                if (fieldIndex === -1 || fieldIndex < 0) {
                    // No '->' found - could be a special case like "options_changed" or "deleted"
                    // Check if the last part is a known field without value change
                    const lastPart = remainingParts[remainingParts.length - 1];
                    if (lastPart === 'deleted') {
                        let question = remainingParts.slice(0, -1).join(':');
                        
                        // Special handling for resum-pagaments
                        if (type === 'resum-pagaments' && (question === '' || question === 'undefined' || question === '[object Object]')) {
                            question = 'Resum de pagaments';
                        }
                        
                        // Special handling for stripe-payment
                        if (type === 'stripe-payment' && (question === '' || question === 'undefined' || question === '[object Object]')) {
                            question = 'Pagar amb targeta';
                        }
                        
                        const typeTranslations = {
                            'ticket': 'Entrada/Pagament',
                            'ticket-v2': 'Entrades múltiples',
                            'ticket-v2-single': 'Entrada única',
                            'multiple-choice': 'Selecció múltiple',
                            'checkbox': 'Caselles de verificació',
                            'short-answer': 'Resposta curta',
                            'paragraph': 'Paràgraf',
                            'image-upload': 'Pujada d\'imatge',
                            'resum-pagaments': 'Resum de pagaments',
                            'text': 'Text informatiu',
                            'assistance': 'Assistència',
                            'stripe-payment': 'Pagament amb targeta'
                        };
                        const typeDisplay = typeTranslations[type] || type;
                        return `🗑️ Pregunta eliminada: "${question}" (${typeDisplay})`;
                    }
                    if (lastPart === 'options_changed') {
                        let question = remainingParts.slice(0, -1).join(':');
                        
                        // Special handling for resum-pagaments
                        if (type === 'resum-pagaments' && (question === '' || question === 'undefined' || question === '[object Object]')) {
                            question = 'Resum de pagaments';
                        }
                        
                        // Special handling for stripe-payment
                        if (type === 'stripe-payment' && (question === '' || question === 'undefined' || question === '[object Object]')) {
                            question = 'Pagar amb targeta';
                        }
                        
                        const typeTranslations = {
                            'ticket': 'Entrada/Pagament',
                            'ticket-v2': 'Entrades múltiples',
                            'ticket-v2-single': 'Entrada única',
                            'multiple-choice': 'Selecció múltiple',
                            'checkbox': 'Caselles de verificació',
                            'short-answer': 'Resposta curta',
                            'paragraph': 'Paràgraf',
                            'image-upload': 'Pujada d\'imatge',
                            'resum-pagaments': 'Resum de pagaments',
                            'text': 'Text informatiu',
                            'assistance': 'Assistència',
                            'stripe-payment': 'Pagament amb targeta'
                        };
                        const typeDisplay = typeTranslations[type] || type;
                        return `✏️ Pregunta modificada: "${question}" (${typeDisplay}) - opcions canviades`;
                    }
                    
                    // Invalid structure or unknown field
                    let question = remainingParts.join(':');
                    
                    // Special handling for resum-pagaments
                    if (type === 'resum-pagaments' && (question === '' || question === 'undefined' || question === '[object Object]')) {
                        question = 'Resum de pagaments';
                    }
                    
                    // Special handling for stripe-payment
                    if (type === 'stripe-payment' && (question === '' || question === 'undefined' || question === '[object Object]')) {
                        question = 'Pagar amb targeta';
                    }
                    
                        const typeTranslations = {
                            'ticket': 'Entrada/Pagament',
                            'ticket-v2': 'Entrades múltiples',
                            'ticket-v2-single': 'Entrada única',
                            'multiple-choice': 'Selecció múltiple',
                            'checkbox': 'Caselles de verificació',
                            'short-answer': 'Resposta curta',
                            'paragraph': 'Paràgraf',
                            'image-upload': 'Pujada d\'imatge',
                            'resum-pagaments': 'Resum de pagaments',
                            'text': 'Text informatiu',
                            'assistance': 'Assistència',
                            'stripe-payment': 'Pagament amb targeta'
                        };
                    const typeDisplay = typeTranslations[type] || type;
                    
                    return `✏️ Pregunta modificada: "${question}" (${typeDisplay}) - canvi detectat`;
                }
                
                // Split into question, field, and value change
                let question = remainingParts.slice(0, fieldIndex).join(':');
                const field = remainingParts[fieldIndex];
                const valueChange = remainingParts.slice(fieldIndex + 1).join(':');
                
                // Special handling for resum-pagaments
                if (type === 'resum-pagaments' && (question === '' || question === 'undefined' || question === '[object Object]')) {
                    question = 'Resum de pagaments';
                }
                
                // Special handling for stripe-payment
                if (type === 'stripe-payment' && (question === '' || question === 'undefined' || question === '[object Object]')) {
                    question = 'Pagar amb targeta';
                }
                
                    const typeTranslations = {
                        'ticket': 'Entrada/Pagament',
                        'ticket-v2': 'Entrades múltiples',
                        'ticket-v2-single': 'Entrada única',
                        'multiple-choice': 'Selecció múltiple',
                        'checkbox': 'Caselles de verificació',
                        'short-answer': 'Resposta curta',
                        'paragraph': 'Paràgraf',
                        'image-upload': 'Pujada d\'imatge',
                        'resum-pagaments': 'Resum de pagaments',
                        'text': 'Text informatiu',
                        'assistance': 'Assistència',
                        'stripe-payment': 'Pagament amb targeta'
                    };
                const typeDisplay = typeTranslations[type] || type;
                
                // Parse the value change
                const [from, to] = valueChange.split('->');
                
                const fieldTranslations = {
                    'question': 'pregunta',
                    'required': 'obligatori',
                    'price': 'preu',
                    'limit': 'límit',
                    'options_changed': 'opcions',
                    'isComprovant': 'és comprovant',
                    'showIf': 'pregunta condicionada',
                    'options_count': 'nombre d\'opcions'
                };
                
                // Handle ticket-v2 option changes
                if (field.startsWith('option_')) {
                    const optionMatch = field.match(/^option_(\d+)_(.+)$/);
                    if (optionMatch) {
                        const optionIndex = parseInt(optionMatch[1]);
                        const optionField = optionMatch[2];
                        
                        const optionFieldTranslations = {
                            'text': 'text',
                            'price': 'preu',
                            'limit': 'límit'
                        };
                        
                        const optionFieldDisplay = optionFieldTranslations[optionField] || optionField;
                        
                        if (optionField === 'price') {
                            return `✏️ Pregunta modificada: "${question}" (${typeDisplay}) - opció ${optionIndex + 1} ${optionFieldDisplay}: ${formatMoney(from)}€ → ${formatMoney(to)}€`;
                        } else if (optionField === 'limit') {
                            const fromDisplay = from === 'sense límit' ? 'sense límit' : from;
                            const toDisplay = to === 'sense límit' ? 'sense límit' : to;
                            return `✏️ Pregunta modificada: "${question}" (${typeDisplay}) - opció ${optionIndex + 1} ${optionFieldDisplay}: ${fromDisplay} → ${toDisplay}`;
                        } else {
                            return `✏️ Pregunta modificada: "${question}" (${typeDisplay}) - opció ${optionIndex + 1} ${optionFieldDisplay}: "${from}" → "${to}"`;
                        }
                    }
                }
                
                const fieldDisplay = fieldTranslations[field] || field;
                
                // Format specific fields better
                if (field === 'question') {
                    return `✏️ Pregunta modificada: "${question}" (${typeDisplay}) - ${fieldDisplay}: ${from} → ${to}`;
                } else if (field === 'price') {
                    return `✏️ Pregunta modificada: "${question}" (${typeDisplay}) - ${fieldDisplay}: ${formatMoney(from)}€ → ${formatMoney(to)}€`;
                } else if (field === 'limit') {
                    const fromDisplay = from === 'sense límit' ? 'sense límit' : from;
                    const toDisplay = to === 'sense límit' ? 'sense límit' : to;
                    return `✏️ Pregunta modificada: "${question}" (${typeDisplay}) - ${fieldDisplay}: ${fromDisplay} → ${toDisplay}`;
                } else if (field === 'showIf') {
                    // Use the new helper function to resolve showIf display names
                    let fromDisplay, toDisplay;
                    
                    try {
                        // Parse the showIf values
                        if (from === 'sense condició') {
                            fromDisplay = 'sense condició';
                        } else {
                            // Extract questionId and optionId from the from string
                            const fromMatch = from.match(/pregunta ([a-f0-9-]+) opció (\d+)/);
                            if (fromMatch) {
                                const questionId = fromMatch[1];
                                const optionId = parseInt(fromMatch[2]);
                                fromDisplay = resolveShowIfDisplay({ questionId, optionId });
                            } else {
                                fromDisplay = from;
                            }
                        }
                        
                        if (to === 'sense condició') {
                            toDisplay = 'sense condició';
                        } else {
                            // Extract questionId and optionId from the to string
                            const toMatch = to.match(/pregunta ([a-f0-9-]+) opció (\d+)/);
                            if (toMatch) {
                                const questionId = toMatch[1];
                                const optionId = parseInt(toMatch[2]);
                                toDisplay = resolveShowIfDisplay({ questionId, optionId });
                            } else {
                                toDisplay = to;
                            }
                        }
                    } catch (e) {
                        fromDisplay = from;
                        toDisplay = to;
                    }
                    
                    return `✏️ Pregunta modificada: "${question}" (${typeDisplay}) - ${fieldDisplay}: ${fromDisplay} → ${toDisplay}`;
                } else if (field === 'required') {
                    const fromText = from === 'true' ? 'obligatori' : 'opcional';
                    const toText = to === 'true' ? 'obligatori' : 'opcional';
                    return `🔧 Propietat 'obligatori' canviada: ${fromText} → ${toText}`;
                } else if (field === 'isComprovant') {
                    const fromText = from === 'true' ? 'és comprovant' : 'no és comprovant';
                    const toText = to === 'true' ? 'és comprovant' : 'no és comprovant';
                    return `✏️ Pregunta modificada: "${question}" (${typeDisplay}) - ${fromText} → ${toText}`;
                } else if (field === 'options_count') {
                    return `✏️ Pregunta modificada: "${question}" (${typeDisplay}) - ${fieldDisplay}: ${from} → ${to}`;
                } else if (field === 'options_changed') {
                    return `✏️ Pregunta modificada: "${question}" (${typeDisplay}) - opcions canviades`;
                } else {
                    return `✏️ Pregunta modificada: "${question}" (${typeDisplay}) - ${fieldDisplay}: ${from} → ${to}`;
                }
            }
            
            // Handle date changes like "openingDate:null->2024-01-01T10:00:00.000Z"
            if (action.includes('->')) {
                const [field, change] = action.split(':');
                const [from, to] = change.split('->');
                
                if (field === 'openingDate') {
                    const fromDisplay = (!from || from === 'null' || from === null) ? 'cap' : applyInverseTimeZone(from).format('DD/MM/YYYY HH:mm');
                    const toDisplay = (!to || to === 'null' || to === null) ? 'cap' : applyInverseTimeZone(to).format('DD/MM/YYYY HH:mm');
                    return `📅 Data d'obertura canviada: ${fromDisplay} → ${toDisplay}`;
                }
                if (field === 'closingDate') {
                    const fromDisplay = (!from || from === 'null' || from === null) ? 'cap' : applyInverseTimeZone(from).format('DD/MM/YYYY HH:mm');
                    const toDisplay = (!to || to === 'null' || to === null) ? 'cap' : applyInverseTimeZone(to).format('DD/MM/YYYY HH:mm');
                    return `📅 Data de tancament canviada: ${fromDisplay} → ${toDisplay}`;
                }
                if (field === 'title') {
                    return `📝 Títol canviat: "${from}" → "${to}"`;
                }
                if (field === 'description') {
                    return `📝 Descripció canviada: "${from}" → "${to}"`;
                }
                if (field === 'required') {
                    const fromText = from === 'true' ? 'obligatori' : 'opcional';
                    const toText = to === 'true' ? 'obligatori' : 'opcional';
                    return `🔧 Propietat 'obligatori' canviada: ${fromText} → ${toText}`;
                }
                if (field === 'hidden') {
                    const fromText = from === 'true' ? 'ocult' : 'visible';
                    const toText = to === 'true' ? 'ocult' : 'visible';
                    return `👁️ Propietat 'ocult' canviada: ${fromText} → ${toText}`;
                }
                
                return `🔧 ${field}: ${from} → ${to}`;
            }
        }
        
        return action;
    };

    const getUserName = (userId) => {
        if (userId === 'anonymous') return 'Usuari anònim';
        if (userId === 'system') return 'Sistema';
        if (userId === 'visitor') return 'VISITANT';
        if (userId === 'unknown' || userId === null || userId === undefined || (typeof userId === 'number' && isNaN(userId))) {
            return 'Usuari desconegut';
        }
        
        // Someone outside the colla who answered on the web (formGuests.js)
        if (String(userId).startsWith('guest_') && !castellersInfo?.[userId]) return guestLabel(userId, null, formGuests, castellersInfo);
        if (!castellersInfo || !castellersInfo[userId]) return `Usuari #${userId}`;
        
        const user = castellersInfo[userId];
        return getCastellerDisplayName(user, `Usuari #${userId}`);
    };

    const getActionIcon = (action) => {
        if (action === 'create') return '🆕';
        if (action === 'update') return '✏️';
        if (action === 'submit') return '📤';
        if (action === 'delete') return '🗑️';
        if (action === 'order_changed') return '🔄';
        
        // Handle detailed form changes
        if (action.includes(':')) {
            const parts = action.split(':');
            
            // Date changes
            if (action.includes('Date:') || (action.includes('->') && (action.includes('openingDate') || action.includes('closingDate')))) {
                return '📅';
            }
            
            // Title and description changes
            if (action.includes('title:') || action.includes('description:')) {
                return '📝';
            }
            
            // Element operations
            if (parts[0] === 'element_added') return '➕';
            if (parts[0] === 'element_removed') return '➖';
            if (parts[0] === 'element_modified') return '✏️';
            
            // Property changes
            if (action.includes('required:') || action.includes('hidden:') || action.includes('showIf:')) return '🔧';
        }
        
        return '🔧';
    };

    const parsedLogs = logs
        .map(parseLogLine)
        .filter(log => log !== null)
        .filter(log => log.action !== 'open') // Filter out "formulari obert" logs
        .sort((a, b) => {
            if (sortOrder === 'desc') {
                return b.timestamp.valueOf() - a.timestamp.valueOf();
            } else {
                return a.timestamp.valueOf() - b.timestamp.valueOf();
            }
        });

    const filteredLogs = parsedLogs.filter(log => {
        if (!filter) return true;
        
        const searchText = filter.toLowerCase();
        const userName = getUserName(log.userId).toLowerCase();
        const action = log.action.toLowerCase();
        const timestamp = log.timestamp.format('DD/MM/YYYY HH:mm').toLowerCase();
        
        return userName.includes(searchText) || 
               action.includes(searchText) || 
               timestamp.includes(searchText);
    });

    const groupLogsByDate = (logs) => {
        const groups = {};
        logs.forEach(log => {
            const dateKey = log.timestamp.format('YYYY-MM-DD');
            if (!groups[dateKey]) {
                groups[dateKey] = [];
            }
            groups[dateKey].push(log);
        });
        return groups;
    };

    const logGroups = groupLogsByDate(filteredLogs);

    // Create a flattened list of items with their date groups for pagination
    const createPaginatedGroups = (groups, limit) => {
        const sortedDateKeys = Object.keys(groups)
            .sort((a, b) => sortOrder === 'desc' ? b.localeCompare(a) : a.localeCompare(b));
        
        const paginatedGroups = {};
        let itemCount = 0;
        
        for (const dateKey of sortedDateKeys) {
            const dateItems = groups[dateKey];
            
            if (itemCount + dateItems.length <= limit) {
                // Include the whole date group
                paginatedGroups[dateKey] = dateItems;
                itemCount += dateItems.length;
            } else {
                // Include partial date group
                const remainingItems = limit - itemCount;
                if (remainingItems > 0) {
                    paginatedGroups[dateKey] = dateItems.slice(0, remainingItems);
                    itemCount += remainingItems;
                }
                break;
            }
        }
        
        return { paginatedGroups, totalShown: itemCount };
    };

    const { paginatedGroups, totalShown } = createPaginatedGroups(logGroups, itemsToShow);
    const hasMoreItems = totalShown < filteredLogs.length;

    const handleLoadMore = () => {
        setItemsToShow(prev => prev + 25);
    };

    if (loading) {
        return (
            <div style={{ marginTop: '2rem' }}>
                <h2>Registre d'activitat</h2>
                <p>Carregant...</p>
            </div>
        );
    }

    return (
        <div style={{ marginTop: '2rem' }}>
            <h2>Registre d'activitat</h2>
            
            <div style={{ 
                display: 'flex', 
                gap: '1rem', 
                marginBottom: '1rem',
                alignItems: 'center',
                flexWrap: 'wrap'
            }}>
                <input
                    type="text"
                    placeholder="Filtrar per usuari, acció o data..."
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    style={{
                        padding: '0.5rem',
                        borderRadius: '4px',
                        border: '1px solid #ddd',
                        flex: 1,
                        minWidth: '200px'
                    }}
                />
                
                <select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    style={{
                        padding: '0.5rem',
                        borderRadius: '4px',
                        border: '1px solid #ddd'
                    }}
                >
                    <option value="desc">Més recent primer</option>
                    <option value="asc">Més antic primer</option>
                </select>

                {filter && (
                    <button
                        onClick={() => setFilter('')}
                        style={{
                            padding: '0.5rem 1rem',
                            borderRadius: '4px',
                            border: '1px solid #ddd',
                            backgroundColor: '#f5f5f5',
                            cursor: 'pointer'
                        }}
                    >
                        Neteja filtre
                    </button>
                )}
            </div>

            {filteredLogs.length === 0 ? (
                <div style={{ 
                    textAlign: 'center', 
                    color: '#666', 
                    fontStyle: 'italic',
                    padding: '2rem'
                }}>
                    {logs.length === 0 ? 
                        'No hi ha activitat registrada per aquest formulari.' :
                        'No s\'han trobat registres amb els filtres aplicats.'
                    }
                </div>
            ) : (
                <div>
                    <div style={{ 
                        border: '1px solid #ddd', 
                        borderRadius: '8px',
                        backgroundColor: 'white'
                    }}>
                        {Object.keys(paginatedGroups)
                            .sort((a, b) => sortOrder === 'desc' ? b.localeCompare(a) : a.localeCompare(b))
                            .map(dateKey => (
                                <div key={dateKey}>
                                    <div style={{
                                        backgroundColor: '#f8f9fa',
                                        padding: '0.75rem 1rem',
                                        borderBottom: '1px solid #ddd',
                                        fontWeight: 'bold',
                                        fontSize: '0.9rem',
                                        color: '#495057'
                                    }}>
                                        {moment(dateKey).format('dddd, D [de] MMMM [de] YYYY')}
                                    </div>
                                    
                                    {paginatedGroups[dateKey].map((log, index) => (
                                        <div
                                            key={index}
                                            style={{
                                                padding: '0.75rem 1rem',
                                                borderBottom: index < paginatedGroups[dateKey].length - 1 ? '1px solid #eee' : 'none',
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: '0.75rem'
                                            }}
                                        >
                                            <div style={{ 
                                                fontSize: '1.2em',
                                                flexShrink: 0,
                                                marginTop: '0.1rem'
                                            }}>
                                                {getActionIcon(log.action)}
                                            </div>
                                            
                                            <div style={{ flex: 1 }}>
                                                <div style={{ 
                                                    fontSize: '0.85rem',
                                                    color: '#666',
                                                    marginBottom: '0.25rem'
                                                }}>
                                                    {log.timestamp.format('HH:mm')} • {getUserName(log.userId)}
                                                </div>
                                                
                                                <div style={{ 
                                                    fontSize: '0.9rem',
                                                    color: '#333'
                                                }}>
                                                    {getActionDisplay(log.action)}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ))}
                    </div>

                    {hasMoreItems && (
                        <div style={{ 
                            textAlign: 'center', 
                            marginTop: '1rem'
                        }}>
                            <button
                                onClick={handleLoadMore}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    borderRadius: '6px',
                                    border: '1px solid #007bff',
                                    backgroundColor: '#007bff',
                                    color: 'white',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem',
                                    fontWeight: '500',
                                    transition: 'background-color 0.2s'
                                }}
                                onMouseOver={(e) => e.target.style.backgroundColor = '#0056b3'}
                                onMouseOut={(e) => e.target.style.backgroundColor = '#007bff'}
                            >
                                Carrega més ({filteredLogs.length - totalShown} restants)
                            </button>
                        </div>
                    )}

                    <div style={{ 
                        marginTop: '1rem', 
                        fontSize: '0.8rem', 
                        color: '#666',
                        textAlign: 'center'
                    }}>
                        {totalShown > 0 && (
                            `Mostrant ${totalShown} de ${filteredLogs.length} entrades`
                        )}
                    </div>
                </div>
            )}

            {/* <div style={{ 
                marginTop: '1rem', 
                fontSize: '0.8rem', 
                color: '#666',
                textAlign: 'center'
            }}>
                {filteredLogs.length > 0 && (
                    `Mostrant ${filteredLogs.length} de ${logs.length} entrades`
                )}
            </div> */}
        </div>
    );
}

export default FormLogs;
